import React, { useEffect, useState } from "react"
import { Button, FormControl, FormGroup, Glyphicon, InputGroup } from "react-bootstrap"
import LoadingAnimation from "../../../../reuseable-components/LoadingAnimation"
import PaginationButtons from "../../../../reuseable-components/PaginationButtons"
import TrainingList from "./TrainingList"
import CreateEditTrainingModal from "./CreateEditTrainingModal"
import TrainingsFilterSidebar from "./TrainingsFilterSidebar"
import axios from "axios"
import swal from 'sweetalert'
import getServerURL from "../../../../functions/getServerURL"
import { handleErrorFetchingData } from "../../../../functions/errorHandling"
import { Col, Grid, Row } from "react-bootstrap"

export default function TrainingContainer() {
  // STATE
  const [trainings, setTrainings] = useState([])
  const [fetchingTrainings, setFetchingTrainings] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [numberOfPages, setNumberOfPages] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const entriesPerPage = 10

  const [filters, setFilters] = useState({
    selected_types: [],
    selected_numbers: [],
    selected_versions: [],
    selected_published_statuses: []
  })

  const [types, setTypes] = useState([])
  const [numbers, setNumbers] = useState([])
  const [versions, setVersions] = useState([])

  const [downloadingFunctionalGroupsForTraining, setDownloadingFunctionalGroupsForTraining] = useState(false)

  const exportAllTrainingsAndGroups = () => {
    setDownloadingFunctionalGroupsForTraining(true)
    axios({
      url: `${getServerURL()}export-functional-groups-for-training/`,
      method: 'GET',
      params: { filters },
      headers: {
        'Authorization': "Bearer " + localStorage.getItem("token")
      },
      responseType: 'blob',
    })
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const date = new Date().toJSON().split("T")[0].split('-')
      const formattedDate = `${date[1]}-${date[2]}-${date[0]}`
      link.setAttribute('download', `Assigned Groups of All Trainings_${formattedDate}.xlsx`)
      swal({
        title: "Your file is ready",
        text: "Click the button to download",
        icon: "success",
        button: 'Download'
      })
      .then(() => {
        document.body.appendChild(link)
        link.click()
      })
    })
    .catch(error => {
      const message = "There was an error downloading your file."
      handleErrorFetchingData(error, message)
    })
    .finally(() => {
      setDownloadingFunctionalGroupsForTraining(false);
    });
  }

  const getTrainings = (page = currentPage) => {
    setFetchingTrainings(true)
    page = page || 1
    setCurrentPage(page)

    const filterParams = {
      search_term: searchTerm,
      types: filters.selected_types.map(type => type),
      numbers: filters.selected_numbers.map(number => number),
      versions: filters.selected_versions.map(version => version),
      published_statuses: filters.selected_published_statuses
    }

    axios.get(`${getServerURL()}trainings/`, {
      headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` },
      params: {
        "is_active": true,
        filters: filterParams,
        "limit": entriesPerPage,
        "offset": entriesPerPage * (page - 1)
      }
    }).then(response => {
      setTrainings(response.data.results)
      setNumberOfPages(Math.ceil(response.data.count / entriesPerPage))
    }).catch(error => {
      handleErrorFetchingData(error)
    }).finally(() => {
      setFetchingTrainings(false)
    })
  }

  const getTrainingMetadata = () => {
    // Fetch types, numbers, and versions for the filters
    axios.get(`${getServerURL()}training-metadata/`, {
      headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
    }).then(response => {
      setTypes(["GD", "POL", "SOP", "WI"]);
      setNumbers(response.data.numbers || []); // Update numbers
      setVersions(response.data.versions || []); // Update versions
    }).catch(error => {
      console.error("Error fetching training metadata:", error);
    });
  };

  const handleEnterPress = (e) => {
    if (e.key === 'Enter') {
      getTrainings(1)
    }
  }

  const handleFilterChange = (filterKey, selectedOptions) => {
    const mappedValues = selectedOptions.map(option => {
      if (typeof option === "object" && option !== null) {
        // Map based on the filter key
        if (filterKey === "selected_types") return option.type;
        if (filterKey === "selected_numbers") return option.number;
        if (filterKey === "selected_versions") return option.version;
        return null; // Fallback for unexpected cases
      }
      return option; // For simple string options
    }).filter(Boolean); // Remove null or undefined values

    setFilters(prevFilters => ({
      ...prevFilters,
      [filterKey]: mappedValues
    }));
  };

  useEffect(() => {
    getTrainings(1)
  }, [filters])

  useEffect(() => {
    getTrainingMetadata();
  }, [])

  return (
    <div id="TrainingContainer">
    <Grid>
    <Row>
    <Col xs={12} md={3}>
    <TrainingsFilterSidebar
      trainingsFilters={filters}
      handleFilterChange={handleFilterChange}
      types={types}
      numbers={numbers}
      versions={versions}
     />
     </Col>
     <Col xs={12} sm={12} md={9} lg={9}>
      <div>
        <div>
          <h5>
            Search
          </h5>
          <FormGroup>
            <InputGroup>
              <FormControl
                disabled={ fetchingTrainings }
                value={ searchTerm }
                onChange={ (e) => setSearchTerm(e.target.value)}
                onKeyDown={ handleEnterPress }
                placeholder="Training Name"
              />
              <InputGroup.Button>
                <Button
                  bsStyle="default"
                  onClick={ getTrainings }
                >
                  <Glyphicon glyph="search" />
                </Button>
              </InputGroup.Button>
            </InputGroup>
          </FormGroup>
        </div>
        <div>
          <CreateEditTrainingModal getTrainings={ getTrainings } />
          <Button className="pull-right"
            disabled={downloadingFunctionalGroupsForTraining}
            onClick={exportAllTrainingsAndGroups}
          >
            {downloadingFunctionalGroupsForTraining ?
              <Glyphicon
                glyph='refresh'
                style={{minWidth: '170px'}}
                className='rotate-btn'
              />
              :
              'Export Assigned Groups for All Trainings'
            }
          </Button>
        </div>
          { numberOfPages > 1 &&
            <center>
            <PaginationButtons
            onClick={ getTrainings }
            numberOfPages={ numberOfPages }
            currentPage={ currentPage }
            buttonsPerPage={ 5 }
            disabled={ fetchingTrainings }
            />
            </center>
          }
      { fetchingTrainings ?
        <LoadingAnimation />
        :
        <TrainingList trainings={ trainings } getTrainings={ getTrainings } getTrainingMetadata={ getTrainingMetadata }  />
      }
      </div>
      </Col>
      </Row>
      </Grid>
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/routes/qms-training/QMSTrainingManager/TrainingContainer/index.js